import { Routes, CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { authGuard } from '@app/core/auth/guards/auth-guard';
import { loginRedirectGuard } from '@app/core/auth/guards/login-redirect-guard';
import { lockedGuard } from '@app/core/auth/guards/locked-guard';
import { analystGuard } from '@app/core/auth/guards/analyst-guard';
import { projectViewerGuard } from '@app/core/auth/guards/project-viewer-guard';
import { ProjectsService } from '@app/core/services/projects.service';

/**
 * Guard for legacy /project/:projectId/admin routes.
 * Sets the active project from the URL param, then redirects to the
 * unified /admin layout, preserving the sub-path when possible.
 */
const projectAdminRedirectGuard: CanActivateFn = (route, state) => {
  const projectsService = inject(ProjectsService);
  const router = inject(Router);
  const id = parseInt(route.params['projectId'], 10);
  if (!isNaN(id)) projectsService.setActiveProject(id);
  // Rewrite /project/123/admin/foo → /admin/project/foo (best-effort)
  const tail = state.url.replace(/^\/project\/\d+\/admin/, '');
  const target = tail ? `/admin/project${tail}` : '/admin';
  return router.createUrlTree([target]);
};

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('@app/features/auth/pages/login/login').then(m => m.Login),
    pathMatch: 'full',
    canActivate: [loginRedirectGuard]
  },
  {
    path: 'login',
    loadComponent: () => import('@app/features/auth/pages/login/login').then(m => m.Login),
    canActivate: [loginRedirectGuard]
  },
  { path: 'locked', loadComponent: () => import('@app/features/auth/pages/locked/locked').then(m => m.Locked) },
  { path: 'reset-password', loadComponent: () => import('@app/features/auth/pages/password-reset/password-reset').then(m => m.PasswordReset) },
  { path: 'accounts/reset/:uid/:token', loadComponent: () => import('@app/features/auth/pages/password-reset-confirm/password-reset-confirm').then(m => m.PasswordResetConfirm) },

  // Legacy /system routes → redirect to unified /admin
  // Specific sub-paths that have a direct mapping are preserved
  { path: 'system', redirectTo: '/admin', pathMatch: 'full' },
  { path: 'system/templates', redirectTo: '/admin/global/templates', pathMatch: 'full' },
  { path: 'system/cache', redirectTo: '/admin/system/cache', pathMatch: 'full' },
  { path: 'system/taxonomies', redirectTo: '/admin/global/taxonomies', pathMatch: 'full' },
  { path: 'system/workflows', redirectTo: '/workflows', pathMatch: 'full' },
  { path: 'system/:any', redirectTo: '/admin', pathMatch: 'full' },

  // User settings routes (all authenticated users)
  {
    path: 'user',
    loadComponent: () => import('@app/layouts/main-layout/main-layout').then(m => m.MainLayout),
    canActivate: [authGuard, lockedGuard],
    children: [
      {
        path: 'profile',
        loadComponent: () => import('@app/features/user/pages/profile/user-profile.component').then(m => m.UserProfileComponent)
      },
      {
        path: 'alerts',
        loadComponent: () => import('@app/features/user/pages/alerts/alerts.component').then(m => m.AlertsComponent)
      }
    ]
  },

  // Project context routes (user and admin together)
  {
    path: 'project/:projectId',
    loadComponent: () => import('@app/layouts/main-layout/main-layout').then(m => m.MainLayout),
    canActivate: [authGuard, lockedGuard],
    children: [
      { path: 'alerts', loadComponent: () => import('@app/features/user/pages/alerts/alerts.component').then(m => m.AlertsComponent) },
      { path: 'dashboard', loadComponent: () => import('@app/features/projects/pages/project-flags-dashboard/project-flags-dashboard.component').then(m => m.ProjectFlagsDashboardComponent) },
      { path: 'listviewer/:viewId', loadComponent: () => import('@app/features/entities/entity-list/entity-list-page.component').then(m => m.EntityListPage) },
      { path: 'econtent/:viewId', loadComponent: () => import('@app/features/content/external-content/external-content.component').then(m => m.ExternalContentComponent) },
      { path: 'detailviewer/:entityId', loadComponent: () => import('@app/features/entities/entity-detail/entity-detail').then(m => m.EntityDetail) }
    ]
  },

  // Project Admin routes — redirect to unified /admin (preserving sub-path)
  {
    path: 'project/:projectId/admin',
    canActivate: [authGuard, lockedGuard, projectViewerGuard, projectAdminRedirectGuard],
    // Guard always redirects; this component/children never actually render
    loadComponent: () => import('@app/layouts/project-admin-layout/project-admin-layout.component').then(m => m.ProjectAdminLayoutComponent),
    children: [{ path: '**', redirectTo: '' }]
  },

  // ── Unified Admin layout ────────────────────────────────────────────────
  {
    path: 'admin',
    loadComponent: () => import('@app/layouts/admin-layout/admin-layout.component').then(m => m.AdminLayoutComponent),
    canActivate: [authGuard, lockedGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@app/features/system-admin/pages/admin-overview/admin-overview.component').then(m => m.AdminOverviewComponent)
      },
      // ── User section ─────────────────────────────────────────────────────
      {
        path: 'user/profile',
        loadComponent: () => import('@app/features/user/pages/profile/user-profile.component').then(m => m.UserProfileComponent)
      },
      {
        path: 'user/alerts',
        loadComponent: () => import('@app/features/user/pages/alerts/alerts.component').then(m => m.AlertsComponent)
      },
      {
        path: 'user/notifications',
        loadComponent: () => import('@app/features/user/pages/notifications/user-notifications-page.component').then(m => m.UserNotificationsPageComponent)
      },
      // ── Project section (uses activeProject from service) ─────────────
      {
        path: 'project/templates',
        loadComponent: () => import('@app/features/system-admin/pages/template-editor/template-editor.component').then(m => m.TemplateEditorComponent)
      },
      {
        path: 'project/taxonomies',
        loadComponent: () => import('@app/features/taxonomy-admin/pages/taxonomy-admin-page.component').then(m => m.TaxonomyAdminPageComponent)
      },
      // Placeholder project pages (implemented progressively)
      { path: 'project/general', loadComponent: () => import('@app/features/project-admin/pages/project-general/project-general-page.component').then(m => m.ProjectGeneralPageComponent) },
      { path: 'project/layout', loadComponent: () => import('@app/features/project-admin/pages/project-layout/project-layout-page.component').then(m => m.ProjectLayoutPageComponent) },
      { path: 'project/users', loadComponent: () => import('@app/features/project-admin/pages/project-users/project-users-page.component').then(m => m.ProjectUsersPageComponent) },
      { path: 'project/notifications', loadComponent: () => import('@app/features/project-admin/pages/project-notifications/project-notifications-page.component').then(m => m.ProjectNotificationsPageComponent) },
      { path: 'project/configurations', loadComponent: () => import('@app/features/project-admin/pages/project-configurations/project-configurations-page.component').then(m => m.ProjectConfigurationsPageComponent) },
      // ── Global section ────────────────────────────────────────────────
      { path: 'global/general',        data: { showAdminProjectSelector: true }, loadComponent: () => import('@app/features/project-admin/pages/project-general/project-general-page.component').then(m => m.ProjectGeneralPageComponent) },
      { path: 'global/layout',         loadComponent: () => import('@app/features/project-admin/pages/project-layout/project-layout-page.component').then(m => m.ProjectLayoutPageComponent) },
      { path: 'global/users',          data: { adminMulti: true }, loadComponent: () => import('@app/features/project-admin/pages/project-users/project-users-page.component').then(m => m.ProjectUsersPageComponent) },
      { path: 'global/notifications',  loadComponent: () => import('@app/features/project-admin/pages/project-notifications/project-notifications-page.component').then(m => m.ProjectNotificationsPageComponent) },
      { path: 'global/configurations', data: { adminMulti: true }, loadComponent: () => import('@app/features/project-admin/pages/project-configurations/project-configurations-page.component').then(m => m.ProjectConfigurationsPageComponent) },
      {
        path: 'global/templates',
        loadComponent: () => import('@app/features/system-admin/pages/template-editor/template-editor.component').then(m => m.TemplateEditorComponent)
      },
      {
        path: 'global/taxonomies',
        data: { adminMulti: true },
        loadComponent: () => import('@app/features/taxonomy-admin/pages/taxonomy-admin-page.component').then(m => m.TaxonomyAdminPageComponent)
      },
      // ── System section (analyst-only guard at route level) ────────────
      {
        path: 'system/cache',
        canActivate: [analystGuard],
        loadComponent: () => import('@app/features/system-admin/pages/cache-manager/cache-manager.component').then(m => m.CacheManagerComponent)
      },
    ]
  },

  // Workflows routes - Analyst workspace (for working with tickets)
  {
    path: 'workflows',
    loadComponent: () => import('@app/features/workflows/layout/workflows-layout.component').then(m => m.WorkflowsLayoutComponent),
    canActivate: [authGuard, lockedGuard, analystGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@app/features/workflows/pages/workflow-list/workflow-list-page.component').then(m => m.WorkflowListPageComponent)
      },
      {
        path: ':workflowId/tickets',
        loadComponent: () => import('@app/features/workflows/pages/ticket-list/ticket-list-page.component').then(m => m.TicketListPageComponent)
      },
      {
        path: ':workflowId/config',
        loadComponent: () => import('@app/features/workflows/pages/workflow-config/workflow-config-page.component').then(m => m.WorkflowConfigPageComponent)
      },
      {
        path: ':workflowId/history',
        loadComponent: () => import('@app/features/workflows/pages/workflow-history/workflow-history-page.component').then(m => m.WorkflowHistoryPageComponent)
      },
      {
        path: 'ticket/:ticketId',
        loadComponent: () => import('@app/features/workflows/pages/ticket-work/ticket-work-page.component').then(m => m.TicketWorkPageComponent)
      }
    ]
  },

  // Legacy profile route (redirect to /user/profile)
  {
    path: 'profile',
    redirectTo: '/user/profile',
    pathMatch: 'full'
  },

  // Main application routes (backward compatibility and legacy routes)
  {
    path: '',
    loadComponent: () => import('@app/layouts/main-layout/main-layout').then(m => m.MainLayout),
    children: [
      { path: 'detailviewer/:entityId', loadComponent: () => import('@app/features/entities/entity-detail/entity-detail').then(m => m.EntityDetail) }, // Backward compatibility
      { path: 'app.intelligence.combo/:viewId', loadComponent: () => import('@app/features/entities/entity-list/entity-list-page.component').then(m => m.EntityListPage) },
      { path: 'app.searchentities/:viewId', loadComponent: () => import('@app/features/entities/entity-list/entity-list-page.component').then(m => m.EntityListPage) },
      { path: 'project/:projectId/otto', loadComponent: () => import('@app/features/otto/otto-embedded.component').then(m => m.OttoEmbeddedComponent) },
      { path: 'project/:projectId/otto/:viewId', loadComponent: () => import('@app/features/otto/otto-embedded.component').then(m => m.OttoEmbeddedComponent) },
    ],
    canActivate: [authGuard, lockedGuard],
  },
  // ── Dev Only: Sentinel Theme Lab ──────────────────────────────────────────
  // Visual audit for all design-system tokens. Remove guard intentionally.
  {
    path: 'theme-lab',
    loadComponent: () => import('@app/features/theme-lab/theme-lab.component').then(m => m.ThemeLabComponent),
  },

  { path: '**', redirectTo: '' }  // Ruta comodín al final
];
